'use client';

import { useEffect, useMemo, useState } from 'react';
import Link from 'next/link';

import type { CarouselApi } from '@/components/ui/carousel/carousel';
import {
  Carousel,
  CarouselContent,
  CarouselItem,
  CarouselNext,
  CarouselPrevious,
} from '@/components/ui/carousel/carousel';
import type { CarouselBlock } from '@impact/types';
import { cn } from '@/lib/utils';
import { EditableMediaCell } from './_editing/EditableMediaCell';
import { usePageEditorStore } from '@/components/page-editor/v2/store/usePageEditor';

interface CarouselBlockRendererProps {
  block: CarouselBlock;
  editing?: boolean;
}

export function CarouselBlockRenderer({ block, editing }: CarouselBlockRendererProps) {
  const {
    slides,
    autoplay = false,
    intervalMs = 4000,
    showDots = true,
    showArrows = true,
    height = 'md',
  } = block.data;
  const [api, setApi] = useState<CarouselApi>();
  const [activeIndex, setActiveIndex] = useState(0);

  const slideCount = slides?.length ?? 0;
  const heightClass = useMemo(
    () =>
      ({
        sm: 'h-56 md:h-72',
        md: 'h-72 md:h-96',
        lg: 'h-[24rem] md:h-[30rem]',
        xl: 'h-[28rem] md:h-[36rem]',
      })[height],
    [height],
  );

  useEffect(() => {
    if (!api) return;
    const onSelect = () => {
      setActiveIndex(api.selectedScrollSnap());
    };
    onSelect();
    api.on('select', onSelect);
    return () => {
      api.off('select', onSelect);
    };
  }, [api]);

  useEffect(() => {
    if (!api || !autoplay || slideCount <= 1 || editing) return;
    const timer = setInterval(() => {
      if (api.canScrollNext()) {
        api.scrollNext();
      } else {
        api.scrollTo(0);
      }
    }, intervalMs);
    return () => clearInterval(timer);
  }, [api, autoplay, editing, intervalMs, slideCount]);

  if (!editing && (!slides || slides.length === 0)) {
    return null;
  }
  const safeSlides = slides ?? [];

  return (
    <div className="my-8">
      <Carousel setApi={setApi} className="w-full">
        <CarouselContent className="-ml-0">
          {safeSlides.map((slide, i) => (
            <CarouselItem
              key={slide.id}
              className="pl-0"
              {...(editing ? { 'data-block-item': String(i) } : {})}
            >
              <div className={cn('relative overflow-hidden rounded-xl', heightClass)}>
                {editing ? (
                  <EditableCarouselSlide
                    blockId={block.id}
                    index={i}
                    src={slide.imageUrl}
                    alt={slide.alt}
                  />
                ) : (
                  /* eslint-disable-next-line @next/next/no-img-element */
                  <img
                    src={slide.imageUrl}
                    alt={slide.alt}
                    className="absolute inset-0 h-full w-full object-cover"
                  />
                )}
                {(slide.caption || (slide.ctaText && slide.ctaUrl)) && (
                  <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent p-4 text-white md:p-6">
                    {slide.caption ? (
                      <p
                        className="text-sm md:text-base"
                        {...(editing ? { 'data-editable': `data.slides[${i}].caption` } : {})}
                      >
                        {slide.caption}
                      </p>
                    ) : null}
                    {slide.ctaText && slide.ctaUrl ? (
                      <Link
                        href={editing ? '#' : slide.ctaUrl}
                        onClick={editing ? (e) => e.preventDefault() : undefined}
                        className="mt-3 inline-flex rounded-md border border-white/60 px-3 py-1.5 text-sm font-medium hover:bg-white/10"
                      >
                        {slide.ctaText}
                      </Link>
                    ) : null}
                  </div>
                )}
              </div>
            </CarouselItem>
          ))}
        </CarouselContent>
        {showArrows && slideCount > 1 ? (
          <>
            <CarouselPrevious className="left-3" />
            <CarouselNext className="right-3" />
          </>
        ) : null}
      </Carousel>
      {showDots && slideCount > 1 ? (
        <div className="mt-3 flex items-center justify-center gap-2">
          {safeSlides.map((slide, index) => (
            <button
              key={slide.id}
              type="button"
              aria-label={`Go to slide ${index + 1}`}
              onClick={() => api?.scrollTo(index)}
              className={cn(
                'h-2.5 w-2.5 rounded-full transition-colors',
                index === activeIndex
                  ? 'bg-primary'
                  : 'bg-muted-foreground/30 hover:bg-muted-foreground/50',
              )}
            />
          ))}
        </div>
      ) : null}
    </div>
  );
}

function EditableCarouselSlide({
  blockId,
  index,
  src,
  alt,
}: {
  blockId: string;
  index: number;
  src: string;
  alt: string;
}) {
  const updateBlockPath = usePageEditorStore((s) => s.updateBlockPath);
  return (
    <EditableMediaCell
      src={src}
      alt={alt}
      className="absolute inset-0 h-full w-full"
      emptyLabel="Click to add slide image"
      onChange={({ src: nextSrc }) => {
        updateBlockPath(blockId, `data.slides[${index}].imageUrl`, nextSrc);
      }}
      onRemove={() => updateBlockPath(blockId, `data.slides[${index}].imageUrl`, '')}
    />
  );
}
